// Project State Type Definitions for Real File System Integration
// These types define the structure of state files stored in .editor-state/

import { GameObject } from './gameobject.types';
import { Animation } from './animation.types';
import { OpenTab, EditorSettings } from './script-editor.types';
import type { SlotScreenGeneratorProjectState } from './slot-screen-generator.types';

// ============================================================================
// File Path Constants
// ============================================================================

/**
 * Relative paths for project state files within the .editor-state directory
 */
export const PROJECT_STATE_FILES = {
  EDITOR_STATE: '.editor-state/editor-state.json',
  SCENE_STATE: '.editor-state/scene-state.json',
  SCRIPT_TABS: '.editor-state/script-tabs.json',
  ANIMATION_STATE: '.editor-state/animation-state.json',
  SLOT_STATE: '.editor-state/slot-state.json',
  SLOT_ASSET_GENERATOR: '.editor-state/slot-asset-generator.json',
  SLOT_SCREEN_GENERATOR: '.editor-state/slot-screen-generator.json',
} as const;

/**
 * The directory name for editor state files
 */
export const EDITOR_STATE_DIR = '.editor-state';

// ============================================================================
// Editor State (editor-state.json)
// ============================================================================

/**
 * Editor-wide state persisted per project
 * Stored in: .editor-state/editor-state.json
 */
export interface ProjectEditorState {
  /** Schema version for migration support */
  version: number;

  /** Last time this state was modified */
  lastModified: number;

  /** Project name as displayed in the editor */
  projectName: string;

  /** Current editor play mode */
  playMode: 'edit' | 'playing' | 'paused';

  /** Last opened scene path (relative to project) */
  lastOpenedScene: string | null;

  /** Recent files list for quick access */
  recentFiles: string[];

  /** Editor window state */
  windowState: {
    width: number;
    height: number;
    x: number;
    y: number;
    isMaximized: boolean;
  };
}

// ============================================================================
// Scene State (scene-state.json)
// ============================================================================

/**
 * Scene hierarchy and game objects state
 * Stored in: .editor-state/scene-state.json
 */
export interface SceneEditorState {
  /** Schema version for migration support */
  version: number;

  /** Last time this state was modified */
  lastModified: number;

  /** Current scene identifier */
  sceneId: string;

  /** Scene name */
  sceneName: string;

  /** Path to the current scene file (null for untitled scenes) */
  currentScenePath: string | null;

  /** All game objects in the scene, keyed by ID */
  gameObjects: Record<string, GameObject>;

  /** IDs of root-level game objects (no parent) */
  rootIds: string[];

  /** Currently selected game object IDs */
  selectedIds: string[];

  /** Scene view camera state */
  sceneViewState: {
    panX: number;
    panY: number;
    zoom: number;
  };
}

// ============================================================================
// Script Editor State (script-tabs.json)
// ============================================================================

/**
 * Script editor open files and tabs state
 * Stored in: .editor-state/script-tabs.json
 */
export interface ScriptEditorProjectState {
  /** Schema version for migration support */
  version: number;

  /** Last time this state was modified */
  lastModified: number;

  /** Currently open tabs */
  openTabs: OpenTab[];

  /** ID of the currently active tab */
  activeTabId: string | null;

  /** Open file paths (for restoring on project open) */
  openFilePaths: string[];

  /** Editor UI settings */
  editorSettings: EditorSettings;

  /** Sidebar width */
  sidebarWidth: number;

  /** Whether sidebar is visible */
  sidebarVisible: boolean;

  /** Bottom panel height */
  bottomPanelHeight: number;

  /** Whether bottom panel is visible */
  bottomPanelVisible: boolean;
}

// ============================================================================
// Animation State (animation-state.json)
// ============================================================================

/**
 * Animation clips and editor state
 * Stored in: .editor-state/animation-state.json
 */
export interface AnimationProjectState {
  /** Schema version for migration support */
  version: number;

  /** Last time this state was modified */
  lastModified: number;

  /** All animations in the project, keyed by ID */
  animations: Record<string, Animation>;

  /** Currently selected animation ID */
  selectedAnimationId: string | null;

  /** Animation editor preferences */
  editorPreferences: {
    snapToFrames: boolean;
    autoKeyframe: boolean;
    showGrid: boolean;
    gridSubdivisions: number;
    onionSkinEnabled: boolean;
  };
}

// ============================================================================
// Slot Editor State (slot-state.json)
// ============================================================================

/**
 * Slot editor state (legacy - kept for project file compatibility)
 * Stored in: .editor-state/slot-state.json
 */
export interface SlotEditorProjectState {
  /** Schema version for migration support */
  version: number;
  /** Last time this state was modified */
  lastModified: number;
}

// ============================================================================
// Combined State Bundle
// ============================================================================

/**
 * Combined bundle of all project state for bulk load/save operations
 */
export interface ProjectStateBundle {
  editorState: ProjectEditorState;
  sceneState: SceneEditorState;
  scriptEditorState: ScriptEditorProjectState;
  animationState: AnimationProjectState;
  slotEditorState: SlotEditorProjectState;
  slotAssetGeneratorState?: SlotAssetGeneratorProjectState;
  slotScreenGeneratorState?: SlotScreenGeneratorProjectState;
}

// ============================================================================
// Default Values
// ============================================================================

/**
 * Default editor state for new projects
 */
export const DEFAULT_EDITOR_STATE: ProjectEditorState = {
  version: 1,
  lastModified: Date.now(),
  projectName: 'Untitled Project',
  playMode: 'edit',
  lastOpenedScene: null,
  recentFiles: [],
  windowState: {
    width: 1280,
    height: 800,
    x: 100,
    y: 100,
    isMaximized: false,
  },
};

/**
 * Default scene state for new projects
 */
export const DEFAULT_SCENE_STATE: SceneEditorState = {
  version: 1,
  lastModified: Date.now(),
  sceneId: '',
  sceneName: 'MainScene',
  currentScenePath: null,
  gameObjects: {},
  rootIds: [],
  selectedIds: [],
  sceneViewState: {
    panX: 0,
    panY: 0,
    zoom: 1,
  },
};

/**
 * Default script editor state for new projects
 */
export const DEFAULT_SCRIPT_EDITOR_STATE: ScriptEditorProjectState = {
  version: 1,
  lastModified: Date.now(),
  openTabs: [],
  activeTabId: null,
  openFilePaths: [],
  editorSettings: {
    theme: 'vs-dark',
    fontSize: 14,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    tabSize: 2,
    insertSpaces: true,
    wordWrap: 'on',
    minimap: true,
    lineNumbers: 'on',
    autoSave: true,
    autoSaveDelay: 1000,
    formatOnSave: true,
    formatOnPaste: true,
    autoCloseBrackets: true,
    autoIndent: true,
  },
  sidebarWidth: 250,
  sidebarVisible: true,
  bottomPanelHeight: 200,
  bottomPanelVisible: true,
};

/**
 * Default animation state for new projects
 */
export const DEFAULT_ANIMATION_STATE: AnimationProjectState = {
  version: 1,
  lastModified: Date.now(),
  animations: {},
  selectedAnimationId: null,
  editorPreferences: {
    snapToFrames: true,
    autoKeyframe: false,
    showGrid: true,
    gridSubdivisions: 4,
    onionSkinEnabled: false,
  },
};

/**
 * Default slot editor state for new projects
 */
export const DEFAULT_SLOT_STATE: SlotEditorProjectState = {
  version: 1,
  lastModified: Date.now(),
};

// ============================================================================
// Slot Asset Generator State (slot-asset-generator.json)
// ============================================================================

/**
 * Persisted slot asset generator state.
 * Only stores user modifications (diff from defaults), not the entire registry.
 */
export interface SlotAssetGeneratorProjectState {
  version: number;
  lastModified: number;
  /** Component overrides: id -> partial fields that differ from defaults */
  componentOverrides: Record<string, Partial<{
    name: string;
    width: number | null;
    height: number | null;
    format: string;
    notes: string;
    referenceImages: string[];
    prompt: string;
  }>>;
  /** IDs of custom (user-added) components with their full definitions */
  customComponents: Array<{
    id: string;
    name: string;
    group: string;
    subgroup?: string;
    width: number | null;
    height: number | null;
    format: string;
    assetPath: string;
    notes: string;
    referenceImages: string[];
    prompt?: string;
  }>;
  /** Last 5 prompts per component ID */
  promptHistory: Record<string, string[]>;
}

export const DEFAULT_SLOT_ASSET_GENERATOR_STATE: SlotAssetGeneratorProjectState = {
  version: 1,
  lastModified: 0,
  componentOverrides: {},
  customComponents: [],
  promptHistory: {},
};

// ============================================================================
// Slot Screen Generator State (slot-screen-generator.json)
// ============================================================================

export type { SlotScreenGeneratorProjectState };

export const DEFAULT_SLOT_SCREEN_GENERATOR_STATE: SlotScreenGeneratorProjectState = {
  version: 1,
  lastModified: 0,
  screens: {} as SlotScreenGeneratorProjectState['screens'],
};

/**
 * Default bundle with all default states
 */
export const DEFAULT_PROJECT_STATE_BUNDLE: ProjectStateBundle = {
  editorState: DEFAULT_EDITOR_STATE,
  sceneState: DEFAULT_SCENE_STATE,
  scriptEditorState: DEFAULT_SCRIPT_EDITOR_STATE,
  animationState: DEFAULT_ANIMATION_STATE,
  slotEditorState: DEFAULT_SLOT_STATE,
  slotScreenGeneratorState: DEFAULT_SLOT_SCREEN_GENERATOR_STATE,
};
